// FaqPage.tsx

import React, { useMemo, useState } from "react";
import { SaleFaqSec, TechFaqSec, OperationFaqSec } from "./FaqInternalSec.tsx";

import SearchIcon from "@mui/icons-material/Search";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import DescriptionBox from "../../components/DescriptionBox/index.tsx";

const FaqPage = () => {
  const [activeSection, setActiveSection] = useState("sales");

  const [search, setSearch] = useState("");

  const sections = [
    {
      id: "sales",
      title: "Sales Queries",
      subtitle: "Pricing, onboarding & plans",
      icon: <CampaignOutlinedIcon fontSize="small" />,
      component: <SaleFaqSec searchValue={search} />,
    },

    {
      id: "technical",
      title: "Technical Support",
      subtitle: "Devices, streams & issues",
      icon: <SupportAgentOutlinedIcon fontSize="small" />,
      component: <TechFaqSec searchValue={search} />,
    },

    {
      id: "operations",
      title: "Operations",
      subtitle: "Reports & monitoring",
      icon: <SettingsSuggestOutlinedIcon fontSize="small" />,
      component: <OperationFaqSec searchValue={search} />,
    },
  ];

  const activeComponent = useMemo(() => {
    return (
      sections.find((item) => item.id === activeSection)?.component || (
        <SaleFaqSec searchValue={search} />
      )
    );
  }, [activeSection, search]);

  return (
    <div
      className="
        relative
        w-full
      "
    >
      {/* Main Layout */}
      <div
        className="
          grid
          grid-cols-1
          xl:grid-cols-[320px_minmax(0,1fr)]
          gap-5
          items-start
        "
      >
        {/* Sidebar */}
        <div
          className="
            sticky
            top-5
            card
            z-[5]
          "
        >
          <div
            className="
              flex
              items-center
              justify-between
              mb-4
            "
          >
            <DescriptionBox title="FAQ CATEGORIES" subTitle="Browse support topics" />

            <div
              className="
                h-[38px]
                w-[38px]
                rounded-full
                border
                border-[rgba(212,168,74,0.18)]
                bg-[rgba(212,168,74,0.08)]
                flex
                items-center
                justify-center
                text-[#d4a84a]
              "
            >
              <SupportAgentOutlinedIcon sx={{ fontSize: 18 }} />
            </div>
          </div>

          <div
            className="
              flex
              xl:flex-col
              gap-3
              overflow-x-auto
              scrollbar-hide
            "
          >
            {sections.map((item) => {
              const active = activeSection === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`
                    group
                    relative
                    overflow-hidden
                    min-w-[240px]
                    xl:min-w-0
                    xl:w-full
                    rounded-[20px]
                    border
                    text-left
                    p-4
                    transition-all
                    duration-300

                    ${
                      active
                        ? `
                          border-[rgba(212,168,74,0.28)]
                          bg-[linear-gradient(135deg,rgba(212,168,74,0.14),rgba(212,168,74,0.04))]
                          shadow-[0_12px_30px_rgba(212,168,74,0.08)]
                        `
                        : `
                          border-[rgb(var(--border-primary))]
                          bg-[rgb(var(--surface-2))]
                          hover:border-[rgba(212,168,74,0.14)]
                          hover:translate-y-[-2px]
                        `
                    }
                  `}
                >
                  {/* Hover glow */}
                  <div
                    className="
                      absolute
                      inset-0
                      opacity-0
                      group-hover:opacity-100
                      transition-opacity
                      duration-300
                    "
                    style={{
                      background:
                        "radial-gradient(circle at top right, rgba(212,168,74,0.08), transparent 60%)",
                    }}
                  />

                  <div className="relative z-[2]">
                    <div
                      className="
                        flex
                        items-start
                        justify-between
                        gap-3
                      "
                    >
                      <div
                        className={`
                          h-[42px]
                          w-[42px]
                          rounded-[14px]
                          flex
                          items-center
                          justify-center
                          transition-all
                          duration-300

                          ${
                            active
                              ? `
                                bg-[rgba(212,168,74,0.14)]
                                text-[#d4a84a]
                              `
                              : `
                                bg-[rgb(var(--surface-3))]
                                text-[rgb(var(--text-secondary))]
                              `
                          }
                        `}
                      >
                        {item.icon}
                      </div>

                      {active && (
                        <div
                          className="
                            h-[9px]
                            w-[9px]
                            rounded-full
                            bg-[#d4a84a]
                            shadow-[0_0_16px_rgba(212,168,74,0.9)]
                          "
                        />
                      )}
                    </div>

                    <div className="mt-4">
                      <div
                        className="
                          text-[15px]
                          font-semibold
                          text-[rgb(var(--text-primary))]
                        "
                      >
                        {item.title}
                      </div>

                      <div
                        className="
                          mt-1
                          text-[12px]
                          leading-[1.6]
                          text-[rgb(var(--text-muted))]
                        "
                      >
                        {item.subtitle}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div
          className="
            card
            overflow-hidden
          "
        >
          {activeComponent}
        </div>
      </div>
    </div>
  );
};

export default FaqPage;
